<template>
  <div class="ts" :class="{ 'ts--open': open }">
    <button :id="id" ref="trigger" type="button" class="form-control ts-trigger" aria-haspopup="dialog"
            :aria-expanded="open ? 'true' : 'false'" @click="toggle">
      <span :class="modelValue ? 'ts-value' : 'ts-placeholder'">{{ modelValue || placeholder }}</span>
      <font-awesome-icon :icon="['fas', 'clock']" class="ts-icon" aria-hidden="true" />
    </button>
    <div v-if="open" class="ts-panel" role="dialog" :aria-label="ariaLabel" @keydown.esc.stop.prevent="close(true)">
      <div class="ts-columns">
        <div ref="hours" class="ts-column" role="listbox" aria-label="Time">
          <button v-for="h in hours" :key="'h' + h" type="button" role="option" class="ts-option"
                  :class="{ 'ts-option--on': h === hour }" :aria-selected="h === hour ? 'true' : 'false'"
                  :disabled="hourOff(h)" :data-v="h" @click="pickHour(h)">{{ h }}</button>
        </div>
        <div ref="minutes" class="ts-column" role="listbox" aria-label="Minutt">
          <button v-for="m in minutes" :key="'m' + m" type="button" role="option" class="ts-option"
                  :class="{ 'ts-option--on': m === minute }" :aria-selected="m === minute ? 'true' : 'false'"
                  :disabled="minuteOff(m)" :data-v="m" @click="pickMinute(m)">{{ m }}</button>
        </div>
      </div>
      <div class="ts-foot">
        <button v-if="clearable" type="button" class="ts-clear" @click="clear">Fjern</button>
        <button type="button" class="ts-ok" @click="close(true)">OK</button>
      </div>
    </div>
  </div>
</template>

<script>
const pad = (n) => String(n).padStart(2, "0");

/**
 * A 24-hour time picker that looks like a browser's own time field — a box with a clock — and is chosen by clicking:
 * an hour column (00–23) and a minute column.
 *
 * Not `<input type="time">`: a browser draws that one in ITS OWN language, so an English Chrome shows AM/PM on a
 * Norwegian page, and nothing in the page can change it. Not the arrow-stepping TimePicker either, which suits
 * opening hours in quarter hours but not finding «about 18:42».
 *
 * v-model is «HH:mm», or "" for no time. With `clearable`, «Fjern» puts it back to "".
 *
 * `min` and `max` («HH:mm», inclusive) grey out what lies outside, so a «Til» before its «Fra» cannot be picked:
 * bind the other field to them. A time is never emitted outside them — picking an hour moves the minute inside.
 */
export default {
  name: "TimeSelect",
  props: {
    modelValue: { type: String, default: "" },
    id: { type: String, default: undefined },
    placeholder: { type: String, default: "--:--" },
    ariaLabel: { type: String, default: "Velg klokkeslett" },
    minuteStep: { type: Number, default: 1 },
    clearable: { type: Boolean, default: true },
    min: { type: String, default: "" },
    max: { type: String, default: "" }
  },
  emits: ["update:modelValue"],
  data() {
    return { open: false };
  },
  computed: {
    hours() { return Array.from({ length: 24 }, (_, i) => pad(i)); },
    minutes() {
      const step = Math.max(1, Math.min(30, this.minuteStep));
      return Array.from({ length: Math.ceil(60 / step) }, (_, i) => pad(i * step));
    },
    hour() { return this.modelValue ? this.modelValue.slice(0, 2) : ""; },
    minute() { return this.modelValue ? this.modelValue.slice(3, 5) : ""; },
    /** The hour a minute belongs to before one is chosen: now, moved inside min and max. */
    fallbackHour() {
      let h = pad(new Date().getHours());
      if (this.min && h < this.min.slice(0, 2)) { h = this.min.slice(0, 2); }
      if (this.max && h > this.max.slice(0, 2)) { h = this.max.slice(0, 2); }
      return h;
    }
  },
  beforeUnmount() {
    document.removeEventListener("pointerdown", this.outside, true);
  },
  methods: {
    toggle() {
      if (this.open) {
        this.close(false);
        return;
      }
      this.open = true;
      // pointerdown, not mousedown: a finger on Android fires it at once, with no emulated mouse event to wait for.
      document.addEventListener("pointerdown", this.outside, true);
      // Show the chosen time, or the time it is now, without the page jumping.
      this.$nextTick(() => {
        this.scrollTo(this.$refs.hours, this.hour || this.fallbackHour);
        const firstFree = this.minutes.find((m) => !this.minuteOff(m)) || "00";
        this.scrollTo(this.$refs.minutes, this.minute || firstFree);
      });
    },
    close(refocus) {
      this.open = false;
      document.removeEventListener("pointerdown", this.outside, true);
      if (refocus && this.$refs.trigger) { this.$refs.trigger.focus(); }
    },
    outside(event) {
      if (!this.$el.contains(event.target)) { this.close(false); }
    },
    scrollTo(column, value) {
      if (!column) { return; }
      const option = column.querySelector('[data-v="' + value + '"]');
      if (option) { column.scrollTop = option.offsetTop - column.clientHeight / 2 + option.clientHeight / 2; }
    },
    inRange(time) {
      return (!this.min || time >= this.min) && (!this.max || time <= this.max);
    },
    /** A real boolean: Vue renders `disabled=""` for an empty string, which DISABLES the button. */
    hourOff(h) {
      return Boolean((this.min && h < this.min.slice(0, 2)) || (this.max && h > this.max.slice(0, 2)));
    },
    minuteOff(m) {
      return !this.inRange((this.hour || this.fallbackHour) + ":" + m);
    },
    pickHour(h) {
      // Keep the minute if it still fits this hour, otherwise the nearest one that does.
      let m = this.minute || "00";
      if (!this.inRange(h + ":" + m)) {
        const fits = this.minutes.filter((x) => this.inRange(h + ":" + x));
        if (!fits.length) { return; }
        m = h + ":" + m < (this.min || "") ? fits[0] : fits[fits.length - 1];
      }
      this.$emit("update:modelValue", h + ":" + m);
      this.$nextTick(() => this.scrollTo(this.$refs.minutes, m));
    },
    /** A minute completes the time, as in the browser's own picker. */
    pickMinute(m) {
      const time = (this.hour || this.fallbackHour) + ":" + m;
      if (!this.inRange(time)) { return; }
      this.$emit("update:modelValue", time);
      this.close(true);
    },
    clear() {
      this.$emit("update:modelValue", "");
      this.close(true);
    }
  }
};
</script>

<style scoped>
.ts { position: relative; }
.ts-trigger {
  display: flex; align-items: center; justify-content: space-between; gap: 8px; text-align: left; cursor: pointer;
  background-color: var(--bo-surface); color: var(--bo-ink);
}
.ts-placeholder { color: var(--bo-muted); }
.ts-value { font-variant-numeric: tabular-nums; }
.ts-icon { color: var(--bo-muted); }
.ts--open .ts-trigger { border-color: #ECB109; box-shadow: 0 0 0 0.2rem rgba(236, 177, 9, 0.25); }

.ts-panel {
  position: absolute; top: calc(100% + 4px); left: 0; z-index: 1050; width: max-content; min-width: 100%;
  background: var(--bo-surface); border: 1px solid var(--bo-line); border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18); padding: 8px;
}
.ts-columns { display: flex; gap: 6px; justify-content: center; }
.ts-column {
  display: flex; flex-direction: column; gap: 2px; max-height: 224px; overflow-y: auto; padding: 2px 4px;
  scrollbar-width: thin; position: relative;
}
.ts-option {
  border: 0; background: transparent; color: var(--bo-ink); border-radius: 6px; min-width: 48px; min-height: 34px;
  font-variant-numeric: tabular-nums; font-size: 0.95rem; cursor: pointer;
}
.ts-option:hover { background: var(--bo-surface-2); }
.ts-option:focus-visible { outline: 2px solid #ECB109; outline-offset: -2px; }
.ts-option--on, .ts-option--on:hover { background: #ECB109; color: #fff; font-weight: 600; }
.ts-option:disabled, .ts-option:disabled:hover { background: transparent; color: var(--bo-muted-2); opacity: 0.45; cursor: not-allowed; }
.ts-foot { display: flex; justify-content: flex-end; gap: 6px; margin-top: 8px; padding-top: 8px; border-top: 1px solid var(--bo-line); }
.ts-clear, .ts-ok { border-radius: 6px; padding: 4px 12px; font-size: 0.9rem; min-height: 32px; cursor: pointer; transition: all .2s ease; }
.ts-clear { background: transparent; border: 1px solid var(--bo-line); color: var(--bo-ink-2); }
.ts-ok { background: #ECB109; border: 1px solid #ECB109; color: #fff; }
.ts-clear:hover, .ts-ok:hover { transform: translateY(-1px); }

/*
 * A finger — the Sunmi D3, a tablet, a phone (touch.css explains why this asks about the pointer, not the width).
 * Every target reaches the 48px floor, a column scrolls with momentum and snaps to an option without dragging the
 * page behind it, and the feedback is :active, because Android latches :hover on the last option tapped.
 */
@media (pointer: coarse) {
  .ts-trigger { min-height: var(--bo-touch-target, 48px); }
  .ts-panel { padding: 10px; }
  .ts-columns { gap: 10px; }
  .ts-column {
    max-height: 288px; overscroll-behavior: contain; scroll-snap-type: y proximity; -webkit-overflow-scrolling: touch;
  }
  .ts-option {
    min-height: var(--bo-touch-target, 48px); min-width: 72px; font-size: 1.15rem; scroll-snap-align: center;
    touch-action: pan-y;
  }
  .ts-option:hover { background: transparent; }
  .ts-option:active { background: var(--bo-surface-2); }
  .ts-option--on, .ts-option--on:hover, .ts-option--on:active { background: #ECB109; color: #fff; }
  .ts-clear, .ts-ok { min-height: var(--bo-touch-target, 48px); padding: 10px 20px; font-size: 1rem; }
  .ts-clear:hover, .ts-ok:hover { transform: none; }
  .ts-clear:active, .ts-ok:active { filter: brightness(0.92); }
}
</style>
